import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, ArrowRight, CalendarDays } from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";

function emptyForm() {
  const today = new Date().toISOString().slice(0, 10);
  return {
    title: "", description: "", agenda: "",
    meeting_date: today, start_time: "10:00", end_time: "11:00",
    participants: "", language: "auto",
  };
}

const FALLBACK_LANGUAGES = [
  { code: "auto",  label: "Auto-detect" },
  { code: "multi", label: "Multi-lingual (mixed)" },
  { code: "en",    label: "English" },
  { code: "hi",    label: "Hindi" },
  { code: "ta",    label: "Tamil" },
  { code: "te",    label: "Telugu" },
  { code: "es",    label: "Spanish" },
  { code: "ml",    label: "Malayalam" },
  { code: "kn",    label: "Kannada" },
];

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);

  const load = () =>
    api.get("/meetings")
      .then(({ data }) => setMeetings(data))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  // Fetch language list from backend so frontend doesn't drift from /api/meta/languages.
  useEffect(() => {
    api.get("/meta/languages")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setLanguages(data);
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const agendaItems = form.agenda
      .split("\n").map((s) => s.trim()).filter(Boolean);
    if (agendaItems.length === 0) {
      toast.error("Agenda is required — please add at least one agenda item.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        agenda: agendaItems,
        meeting_date: form.meeting_date,
        start_time: form.start_time,
        end_time: form.end_time,
        language: form.language,
        participants: form.participants
          .split(",").map((s) => s.trim()).filter(Boolean)
          .map((p) => ({ name: p, email: p.includes("@") ? p : undefined })),
      };
      await api.post("/meetings", payload);
      toast.success("Meeting created");
      setOpen(false);
      setForm(emptyForm());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={TEST_IDS.meetingsRoot} className="p-6 md:p-8 lg:p-12">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="eyebrow flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> Meetings</div>
          <h1 className="font-heading text-4xl sm:text-5xl mt-3 leading-none">Meetings.</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger data-testid="create-meeting-trigger" asChild>
            <Button
              data-testid={TEST_IDS.createMeetingBtn}
              className="rounded-full h-11 px-5 font-bold shadow-lift"
            >
              <Plus className="w-4 h-4 mr-2" /> New meeting
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-3xl max-h-[88vh] overflow-y-auto yl-scroll">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                Schedule meeting
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Add title, date, time, agenda and participants. Participants can be names or comma-separated emails.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} data-testid="create-meeting-form" className="space-y-4">
              <div>
                <Label className="eyebrow text-[9px]">Title</Label>
                <Input
                  data-testid={TEST_IDS.meetingTitleInput}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="rounded-xl mt-1.5 h-11"
                  required
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="eyebrow text-[9px]">Date</Label>
                  <Input
                    data-testid={TEST_IDS.meetingDateInput}
                    type="date"
                    value={form.meeting_date}
                    onChange={(e) => setForm({ ...form, meeting_date: e.target.value })}
                    className="rounded-xl mt-1.5 h-11"
                    required
                  />
                </div>
                <div>
                  <Label className="eyebrow text-[9px]">Start</Label>
                  <Input
                    data-testid={TEST_IDS.meetingStartTimeInput}
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    className="rounded-xl mt-1.5 h-11"
                    required
                  />
                </div>
                <div>
                  <Label className="eyebrow text-[9px]">End</Label>
                  <Input
                    data-testid={TEST_IDS.meetingEndTimeInput}
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    className="rounded-xl mt-1.5 h-11"
                  />
                </div>
              </div>
              <div>
                <Label className="eyebrow text-[9px]">Description</Label>
                <Textarea
                  data-testid={TEST_IDS.meetingDescriptionInput}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="rounded-xl mt-1.5 h-11"
                  rows={2}
                />
              </div>
              <div>
                <Label className="eyebrow text-[9px]">
                  Agenda <span className="text-rose-600">*</span> <span className="opacity-60">(one item per line)</span>
                </Label>
                <Textarea
                  data-testid={TEST_IDS.meetingAgendaInput}
                  value={form.agenda}
                  onChange={(e) => setForm({ ...form, agenda: e.target.value })}
                  className="rounded-xl mt-1.5 h-11"
                  rows={3}
                  required
                  placeholder="e.g.&#10;Q3 numbers review&#10;Hiring pipeline&#10;Action items from last week"
                />
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  Agenda is required — it powers agenda-drift detection and the meeting health score.
                </p>
              </div>
              <div>
                <Label className="eyebrow text-[9px]">
                  Participants (comma-separated names or emails)
                </Label>
                <Input
                  data-testid={TEST_IDS.meetingParticipantInput}
                  value={form.participants}
                  onChange={(e) => setForm({ ...form, participants: e.target.value })}
                  className="rounded-xl mt-1.5 h-11"
                />
              </div>
              <div>
                <Label className="eyebrow text-[9px]">
                  Primary language
                </Label>
                <Select
                  data-testid="meeting-language-select-root"
                  value={form.language}
                  onValueChange={(v) => setForm({ ...form, language: v })}
                >
                  <SelectTrigger
                    data-testid="meeting-language-select"
                    className="rounded-xl mt-1.5 h-11"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((l) => (
                      <SelectItem key={l.code} data-testid={`meeting-lang-${l.code}`} value={l.code}>
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  The Yanthra AI Agent auto-detects, but a hint improves accuracy. For meetings that switch between languages (e.g. Tamil + English, Spanish + English), pick <strong>Multi-lingual</strong> — the recording is chunked into ~10-min slices and each slice is detected independently. Long meetings up to 3&nbsp;hours are supported (18 chunks max).
                </p>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  data-testid={TEST_IDS.meetingSaveBtn}
                  disabled={saving}
                  className="rounded-full h-11 px-6 bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))] font-bold"
                >
                  {saving ? "Saving…" : "Create meeting"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <MeetingsOverview meetings={meetings} loading={loading} />

      <div className="yl-surface overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              <th className="px-5 py-3 eyebrow font-bold">Title</th>
              <th className="px-5 py-3 eyebrow font-bold">Date</th>
              <th className="px-5 py-3 eyebrow font-bold">Time</th>
              <th className="px-5 py-3 eyebrow font-bold">Status</th>
              <th className="px-5 py-3 eyebrow font-bold w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && meetings.length === 0 && (
              <tr><td colSpan={5} className="p-12 text-center text-muted-foreground">No meetings yet. Click <strong>New meeting</strong> to get started.</td></tr>
            )}
            {meetings.map((m) => (
              <tr
                key={m.id}
                data-testid={TEST_IDS.meetingRow}
                className="border-t border-border hover:bg-secondary/40 transition-colors"
              >
                <td className="px-5 py-4">
                  <div className="font-semibold">{m.title}</div>
                  {m.description && (
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{m.description}</div>
                  )}
                </td>
                <td className="px-5 py-4">{m.meeting_date}</td>
                <td className="px-5 py-4">{m.start_time}{m.end_time ? ` – ${m.end_time}` : ""}</td>
                <td className="px-5 py-4"><StatusBadge value={m.status} /></td>
                <td className="px-5 py-4">
                  <Link
                    to={`/meetings/${m.id}`}
                    data-testid={TEST_IDS.meetingOpenBtn}
                    className="inline-flex items-center text-xs font-semibold text-primary hover:underline underline-offset-4"
                  >
                    Open <ArrowRight className="w-3 h-3 ml-1" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile card list */}
      <div className="md:hidden space-y-3" data-testid="meetings-mobile-list">
        {loading && <div className="yl-surface p-6 text-sm text-muted-foreground">Loading…</div>}
        {!loading && meetings.length === 0 && (
          <div className="yl-surface p-8 text-center text-sm text-muted-foreground">
            No meetings yet. Tap <strong>New meeting</strong> to get started.
          </div>
        )}
        {(meetings || []).map((m) => (
          <Link
            key={m.id}
            to={`/meetings/${m.id}`}
            data-testid={`meeting-card-${m.id}`}
            className="yl-surface yl-hover-lift block p-5"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-bold text-sm leading-snug">{m.title}</div>
                <div className="font-mono text-[10px] text-muted-foreground mt-2 uppercase tracking-wider">
                  {m.meeting_date} · {m.start_time}{m.end_time ? ` – ${m.end_time}` : ""}
                </div>
              </div>
              <StatusBadge value={m.status} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const STATUS_PALETTE = {
  Scheduled: "hsl(var(--muted-foreground))", "In Progress": "hsl(var(--chart-2))",
  Processing: "hsl(var(--chart-3))", Completed: "hsl(var(--ink-success))",
  Cancelled: "hsl(var(--border))", Draft: "hsl(var(--muted-foreground))",
};

function MeetingsOverview({ meetings, loading }) {
  const list = meetings || [];
  const byStatus = list.reduce((acc, m) => {
    const s = m.status || "Scheduled";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const data = Object.entries(byStatus).map(([name, value]) => ({ name, value }));
  const total = list.length;
  const completed = byStatus.Completed || 0;
  const active = (byStatus["In Progress"] || 0) + (byStatus.Processing || 0);
  const upcoming = byStatus.Scheduled || 0;
  const cells = data.length ? data : [{ name: "None", value: 1 }];

  const cardCls = "yl-surface yl-hover-lift yl-beam p-5";

  return (
    <div data-testid="meetings-overview" className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
      <div className={cardCls}>
        <div className="eyebrow">Total meetings</div>
        <div className="font-heading text-4xl mt-2">{loading ? "—" : total}</div>
        <div className="text-xs text-muted-foreground mt-1">across your workspace</div>
      </div>
      <div className={cardCls}>
        <div className="eyebrow">Completed</div>
        <div className="font-heading text-4xl mt-2" style={{ color: "hsl(var(--ink-success))" }}>{loading ? "—" : completed}</div>
        <div className="text-xs text-muted-foreground mt-1">{total ? Math.round((completed / total) * 100) : 0}% of all meetings</div>
      </div>
      <div className={cardCls}>
        <div className="eyebrow">Live &amp; processing</div>
        <div className="font-heading text-4xl mt-2" style={{ color: "hsl(var(--ink-info))" }}>{loading ? "—" : active}</div>
        <div className="text-xs text-muted-foreground mt-1">{upcoming} scheduled next</div>
      </div>
      <div className={cardCls}>
        <div className="eyebrow mb-1">By status</div>
        <div className="flex items-center gap-3">
          <div style={{ width: 90, height: 90, position: "relative" }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={cells} dataKey="value" innerRadius={28} outerRadius={44} paddingAngle={2} stroke="none">
                  {(cells || []).map((d) => <Cell key={d.name} fill={STATUS_PALETTE[d.name] || "hsl(var(--border))"} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ position: "absolute", inset: 0 }} className="flex flex-col items-center justify-center">
              <div className="font-heading text-xl leading-none">{total}</div>
            </div>
          </div>
          <div className="flex-1 space-y-1">
            {(data || []).slice(0, 4).map((d) => (
              <div key={d.name} className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: STATUS_PALETTE[d.name] || "hsl(var(--border))" }} />{d.name}</span>
                <span className="font-semibold">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
